import { useI18n } from "@/context/I18nContext";
import { LANGS } from "@/i18n/translations";
import { equivalentPath } from "@/i18n/pages";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Flag = ({ cc, label = "", className = "" }) => (
  <img
    src={`https://flagcdn.com/w40/${cc}.png`}
    srcSet={`https://flagcdn.com/w80/${cc}.png 2x`}
    alt={label || cc.toUpperCase()}
    width={20}
    height={15}
    loading="lazy"
    className={`h-[15px] w-[20px] rounded-[2px] object-cover shadow-sm ${className}`}
  />
);

export default function LanguageSwitcher({ variant = "ghost" }) {
  const { lang, setLang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  const onSelect = (code) => {
    setLang(code);
    const eq = equivalentPath(location.pathname, code);
    if (eq) navigate(eq);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="lang-switcher"
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 h-9 text-sm font-medium transition-colors ${variant === "outline" ? "border border-border" : "hover:bg-muted"}`}
        >
          <Flag cc={current.cc} label={current.label} />
          <span className="uppercase">{current.code}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[170px]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => onSelect(l.code)}
            data-testid={`lang-option-${l.code}`}
            className={`gap-2.5 ${l.code === lang ? "bg-muted font-semibold" : ""}`}
          >
            <Flag cc={l.cc} label={l.label} />
            <span className="flex-1">{l.label}</span>
            {l.code === lang && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
